// Lecture légère des en-têtes d'e-mail : le décodage complet du MIME se fait dans le
// navigateur (voir docs/plan-technique.md, § 2). Rien ici ne doit coûter cher en calcul.
import { parseAuthenticationResults } from "./mail-auth";

const ENCODED_WORD = /=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g;

// Décode les « encoded-words » RFC 2047 (=?UTF-8?B?…?=), courants dans les objets accentués.
export function decodeHeader(value: string | null): string | null {
  if (value === null) return null;
  // Les espaces entre deux encoded-words consécutifs ne font pas partie du texte.
  const joined = value.replace(/\?=\s+=\?/g, "?==?");
  return joined.replace(ENCODED_WORD, (whole, charset: string, encoding: string, text: string) => {
    try {
      const bytes =
        encoding.toUpperCase() === "B" ? base64Bytes(text) : quotedPrintableBytes(text.replace(/_/g, " "));
      return new TextDecoder(charset.toLowerCase()).decode(bytes);
    } catch {
      return whole;
    }
  });
}

function base64Bytes(text: string): Uint8Array {
  return Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
}

function quotedPrintableBytes(text: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "=" && /^[0-9A-Fa-f]{2}$/.test(text.slice(i + 1, i + 3))) {
      out.push(parseInt(text.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      out.push(text.charCodeAt(i));
    }
  }
  return Uint8Array.from(out);
}

export interface Mailbox {
  name: string | null;
  address: string;
}

// Découpe une liste d'adresses (« Nom <a@b>, c@d ») en ignorant les virgules entre guillemets.
export function parseAddressList(value: string | null): Mailbox[] {
  if (!value) return [];
  const parts: string[] = [];
  let current = "";
  let quoted = false;
  let depth = 0;
  for (const ch of value) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && ch === "<") depth++;
    else if (!quoted && ch === ">") depth--;
    if (ch === "," && !quoted && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts.map(parseMailbox).filter((m): m is Mailbox => m !== null);
}

function parseMailbox(raw: string): Mailbox | null {
  const text = raw.trim();
  if (!text) return null;
  const angle = text.match(/^(.*)<([^>]+)>\s*$/);
  if (angle) {
    const name = decodeHeader(angle[1].trim().replace(/^"(.*)"$/, "$1")) || null;
    return { name, address: angle[2].trim().toLowerCase() };
  }
  return { name: null, address: text.toLowerCase() };
}

// Extrait les Message-ID (<…>) d'un en-tête In-Reply-To ou References.
export function parseMessageIds(value: string | null): string[] {
  return value?.match(/<[^>]+>/g) ?? [];
}

// Authenticité de l'expéditeur, d'après le verdict qu'ajoute Cloudflare Email Routing
// (en-tête Authentication-Results de mx.cloudflare.net) :
// - « fail » : DMARC échoue, ou ni SPF ni DKIM ne réussissent (rien ne prouve l'expéditeur) ;
// - « pass » sinon ; null si Cloudflare n'a rien indiqué.
// Un expéditeur peut glisser son propre Authentication-Results : seul le premier, placé en tête
// par Cloudflare au dernier saut, est lu.
export function authVerdict(value: string | null): "pass" | "fail" | null {
  const result = parseAuthenticationResults(value);
  if (result?.source !== "mx.cloudflare.net") return null;
  if (result.checks.dmarc.includes("fail")) return "fail";
  if (result.checks.dmarc.includes("pass")) return "pass";
  if (result.checks.spf.includes("pass") || result.checks.dkim.includes("pass")) return "pass";
  return Object.values(result.checks).some((checks) => checks.length) ? "fail" : null;
}
